import React, { useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check } from "lucide-react";

const produtos = [
  { value: "soja", label: "Soja", icon: "🌱" },
  { value: "milho", label: "Milho", icon: "🌽" },
  { value: "sorgo", label: "Sorgo", icon: "🌾" },
  { value: "trigo", label: "Trigo", icon: "🌾" },
  { value: "feijao", label: "Feijão", icon: "🫘" },
  { value: "arroz", label: "Arroz", icon: "🍚" },
  { value: "cafe", label: "Café", icon: "☕" },
  { value: "algodao", label: "Algodão", icon: "🏵️" },
  { value: "amendoim", label: "Amendoim", icon: "🥜" },
  { value: "girassol", label: "Girassol", icon: "🌻" },
  { value: "outro", label: "Outro", icon: "📦" }
];

export default function EstoqueGraoForm({ armazens, safras, onSuccess, onCancel }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");

  const [formData, setFormData] = useState({
    armazem_id: "",
    produto: "soja",
    safra_id: "",
    quantidade: "",
    unidade: "sacas",
    umidade: "",
    impureza: "",
    classificacao: "padrao",
    custo_medio: "",
    data_entrada: new Date().toISOString().split('T')[0],
    lote: "",
    observacoes: ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Normalize to KG. Cost is always Input(Saca) -> Price/KG
    const qtdInput = parseFloat(formData.quantidade) || 0;
    const custoInputSaca = parseFloat(formData.custo_medio) || 0; // Value per Saca (60kg)

    let multiplicadorQtd = 1;
    if (formData.unidade === 'sacas') multiplicadorQtd = 60;
    if (formData.unidade === 'toneladas') multiplicadorQtd = 1000;

    const quantidadeKg = qtdInput * multiplicadorQtd;
    const custoPorKg = custoInputSaca / 60; // Always divide by 60 since input is per SC

    const data = {
      ...formData,
      propriedade_id: propriedadeId,
      quantidade: quantidadeKg,
      unidade: 'kg', // Always store as KG
      custo_medio: custoPorKg,
      umidade: parseFloat(formData.umidade) || null,
      impureza: parseFloat(formData.impureza) || null
    };

    await agrogestao.entities.EstoqueGrao.create(data);

    // Criar movimentação de entrada
    await agrogestao.entities.MovimentacaoGrao.create({
      propriedade_id: propriedadeId,
      estoque_grao_id: null,
      armazem_id: formData.armazem_id,
      tipo: 'entrada',
      produto: formData.produto,
      quantidade: quantidadeKg,
      unidade: 'kg',
      data: formData.data_entrada,
      origem_destino: 'Ajuste Manual de Estoque',
      preco_unitario: custoPorKg,
      valor_total: quantidadeKg * custoPorKg,
      umidade: parseFloat(formData.umidade) || null,
      impureza: parseFloat(formData.impureza) || null,
      safra_id: formData.safra_id || null
    });

    setSaving(false);
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Armazém *</Label>
          <Select value={formData.armazem_id} onValueChange={(v) => setFormData({ ...formData, armazem_id: v })} required>
            <SelectTrigger>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {armazens.map(a => (
                <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Produto *</Label>
          <Select value={formData.produto} onValueChange={(v) => setFormData({ ...formData, produto: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {produtos.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.icon} {p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Quantidade *</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="0"
            value={formData.quantidade}
            onChange={(e) => setFormData({ ...formData, quantidade: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label>Unidade</Label>
          <Select value={formData.unidade} onValueChange={(v) => setFormData({ ...formData, unidade: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sacas">Sacas (60kg)</SelectItem>
              <SelectItem value="toneladas">Toneladas</SelectItem>
              <SelectItem value="kg">Quilogramas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Umidade (%)</Label>
          <Input
            type="number"
            step="0.1"
            placeholder="14.0"
            value={formData.umidade}
            onChange={(e) => setFormData({ ...formData, umidade: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>Impureza (%)</Label>
          <Input
            type="number"
            step="0.1"
            placeholder="1.0"
            value={formData.impureza}
            onChange={(e) => setFormData({ ...formData, impureza: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>Custo Médio (R$ por Saca)</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="0,00"
            value={formData.custo_medio}
            onChange={(e) => setFormData({ ...formData, custo_medio: e.target.value })}
          />
          <p className="text-xs text-stone-500">Informe o valor referente a uma saca de 60kg</p>
        </div>

        <div className="space-y-2">
          <Label>Data de Entrada</Label>
          <Input
            type="date"
            value={formData.data_entrada}
            onChange={(e) => setFormData({ ...formData, data_entrada: e.target.value })}
          />
        </div>

        {safras.length > 0 && (
          <div className="space-y-2">
            <Label>Safra</Label>
            <Select value={formData.safra_id} onValueChange={(v) => setFormData({ ...formData, safra_id: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>Nenhuma</SelectItem>
                {safras.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label>Classificação</Label>
          <Select value={formData.classificacao} onValueChange={(v) => setFormData({ ...formData, classificacao: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tipo1">Tipo 1</SelectItem>
              <SelectItem value="tipo2">Tipo 2</SelectItem>
              <SelectItem value="tipo3">Tipo 3</SelectItem>
              <SelectItem value="padrao">Padrão</SelectItem>
              <SelectItem value="abaixo_padrao">Abaixo do Padrão</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2 space-y-2">
          <Label>Lote</Label>
          <Input
            placeholder="Identificação do lote"
            value={formData.lote}
            onChange={(e) => setFormData({ ...formData, lote: e.target.value })}
          />
        </div>

        <div className="col-span-2 space-y-2">
          <Label>Observações</Label>
          <Textarea
            placeholder="Observações..."
            value={formData.observacoes}
            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button type="submit" disabled={saving} className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />Cadastrar</>}
        </Button>
      </div>
    </form>
  );
}
